// src/components/notifications/NotificationToasts.tsx
import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Bell } from 'lucide-react';
import confetti from 'canvas-confetti';
import type { AppNotification } from '../../hooks/useNotifications';

type Props = {
  item: AppNotification | null;
  onClose: () => void;
};

const NotificationToasts: React.FC<Props> = ({ item, onClose }) => {
  useEffect(() => {
    if (!item) return;

    // Trigger confetti for accepted applications
    if (item.type === 'application_accepted' || item.type === 'application_accepted_kanban') {
      const duration = 3000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

      function randomInRange(min: number, max: number) {
        return Math.random() * (max - min) + min;
      }

      const interval: any = setInterval(function() {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          return clearInterval(interval);
        }

        const particleCount = 50 * (timeLeft / duration);
        confetti(Object.assign({}, defaults, {
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
        }));
        confetti(Object.assign({}, defaults, {
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
        }));
      }, 250);

      return () => clearInterval(interval);
    }

    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [item, onClose]);

  return (
    <div className="pointer-events-none fixed right-6 top-6 z-[60] flex w-[360px] flex-col gap-2">
      <AnimatePresence>
        {item && (
          <motion.div
            key={item.id}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            className="pointer-events-auto rounded-xl bg-zinc-900/90 text-white shadow-xl ring-1 ring-zinc-800 p-3 flex items-start gap-2"
          >
            <Bell className="h-4 w-4 mt-0.5" />
            <div className="text-sm leading-snug">
              <div className="font-medium">New</div>
              <div className="opacity-90">
                {item.title || item.content || item.message || 'You have a new notification'}
              </div>
            </div>
            <button onClick={onClose} className="ml-auto opacity-70 hover:opacity-100">✕</button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationToasts;
